// Thunks relacionados con el carrito
import { createAsyncThunk } from "@reduxjs/toolkit";
import store from "@/app/store";
import api from "@/services/api";
import {
  carritoSuccess,
  carritoFailure,
  setCarritos,
  updateCarritoInList,
  removeCarritoFromList,
} from "./carritoSlice";

/**
 * 📝 CARRITO - Obtener lista de carritos activos del usuario
 */
export const getListCarritosPorUserId = createAsyncThunk(
  "carrito/getListCarritosPorUserId",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/carritos/user/${store.getState().auth.user.uid}`
      );
      store.dispatch(setCarritos(response.data));
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Error al obtener los carritos";
      store.dispatch(carritoFailure(message));
      return rejectWithValue(message);
    }
  }
);

/**
 * 📝 CARRITO - Crear un nuevo carrito con tickets
 */
export const crearCarrito = createAsyncThunk(
  "carrito/crearCarrito",
  async ({ eventoUid, tickets, ambassadorReferralCode = null }, { rejectWithValue }) => {
    try {
      const response = await api.post("/carritos", {
        eventoUid,
        tickets,
        ambassadorReferralCode,
      });
      // Recargar lista de carritos después de crear
      store.dispatch(getListCarritosPorUserId());
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || error.response?.data?.error || "Error al crear el carrito";
      store.dispatch(carritoFailure(message));
      return rejectWithValue(message);
    }
  }
);

/**
 * 📝 CARRITO - Agregar tickets de un evento a un carrito existente
 */

export const addTicketsFromEventToExistingCart = createAsyncThunk(
  "carrito/addTicketsFromEventToExistingCart",
  async ({ eventoUid, carritoUid, tickets, ambassadorReferralCode = null }, { rejectWithValue }) => {
    try {
      const response = await api.post("/carritos/add-tickets-from-event", {
        eventoUid,
        carritoUid,
        tickets,
        ambassadorReferralCode,
      });
      // Recargar lista de carritos después de agregar tickets
      store.dispatch(getListCarritosPorUserId());
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || error.response?.data?.error || 
        "Error al agregar tickets al carrito existente";
      store.dispatch(carritoFailure(message));
      return rejectWithValue(message);
    }
  }
);

/**
 * 📝 CARRITO - Agregar un ticket a un carrito existente
 */
export const addTicketCarrito = createAsyncThunk(
  "carrito/addTicketCarrito",
  async (
    { carritoUid, ticketUid, idTipoTicket, precioVenta },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`/carritos/${carritoUid}/add-ticket`, {
        idTipoTicket,
        precioVenta,
        ticketUid,
      });
      // Recargar lista de carritos después de agregar
      store.dispatch(getListCarritosPorUserId());
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Error al agregar ticket al carrito";
      store.dispatch(carritoFailure(message));
      return rejectWithValue(message);
    }
  }
);

/**
 * 📝 CARRITO - Remover un ticket de un carrito
 */
export const removeTicketCarrito = createAsyncThunk(
  "carrito/removeTicketCarrito",
  async (
    { carritoUid, ticketUid, idTipoTicket, precioVenta },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`/carritos/${carritoUid}/remove-ticket`, {
        ticketUid,
        idTipoTicket,
        precioVenta,
      });
      // Recargar lista de carritos después de remover
      store.dispatch(getListCarritosPorUserId());
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Error al remover ticket del carrito";
      store.dispatch(carritoFailure(message));
      return rejectWithValue(message);
    }
  }
);

/**
 * 📝 CARRITO - Eliminar un carrito completo
 */
export const eliminarCarrito = createAsyncThunk(
  "carrito/eliminarCarrito",
  async ({ carritoUid }, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/carritos/${carritoUid}`);
      // Recargar lista de carritos después de eliminar
      store.dispatch(getListCarritosPorUserId());
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Error al eliminar el carrito";
      store.dispatch(carritoFailure(message));
      return rejectWithValue(message);
    }
  }
);

/**
 * 📝 CARRITO - Procesar compra de un carrito (LEGACY - para pagos sin pasarela)
 */
export const purchaseCart = createAsyncThunk(
  "carrito/purchaseCart",
  async ({ carritoUid, success, codigoDescuento }, { rejectWithValue }) => {
    try {
      const response = await api.post("/carritos/purchase", {
        carritoUid,
        success,
        codigoDescuento,
      });
      // Recargar lista de carritos después de la compra
      store.dispatch(getListCarritosPorUserId());
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Error al procesar la compra del carrito";
      store.dispatch(carritoFailure(message));
      return rejectWithValue(message);
    }
  }
);

/**
 * 📝 CARRITO - Iniciar proceso de pago con KLAP
 * Obtiene el klapOrderId para abrir el checkout de KLAP
 * 
 * @param {string} carritoUid - UID del carrito a pagar
 * @param {string} ambassadorReferralCode - Código de referido del embajador (opcional)
 * @returns {Promise<{klapOrderId: string}>} - Retorna el klapOrderId
 */
export const payCartWithKlap = createAsyncThunk(
  "carrito/payCartWithKlap",
  async ({ carritoUid, ambassadorReferralCode = null }, { rejectWithValue }) => {
    try {
      // POST /carritos/{carritoUid}/pay-with-klap
      const response = await api.post(`/carritos/${carritoUid}/pay-wklap`, {
        ambassadorReferralCode,
      });
      
      // La respuesta debe contener { klapOrderId: "..." }
      if (!response.data?.klapOrderId) {
        throw new Error("No se recibió klapOrderId de la pasarela de pago");
      }
      
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || error.response?.data?.error?.message || "Error al iniciar el proceso de pago con KLAP";
      const errorCode = error.response?.data?.error?.code;
      store.dispatch(carritoFailure(message));
      return rejectWithValue({ message, code: errorCode });
    }
  }
);